/**
 * The one error type the rest of the app sees for any failed API call.
 *
 * `code` is the backend's top-level code (src/contracts/error-codes.ts). Most domain failures are a
 * generic `CONFLICT` / `UNPROCESSABLE` whose real meaning rides in `details.code` (e.g.
 * `SLOT_ALREADY_HELD`, `PAYMENTS_UNAVAILABLE`) — exposed here as `subCode`. Users module errors
 * use `details.reason` instead (`EMAIL_ALREADY_EXISTS`), so `subCode` falls back to it.
 */
import { ALL_ERROR_CODES, type ErrorCode } from '@/contracts'
import type { ErrorEnvelope } from './types'

export type ApiErrorKind =
  /** The server answered with the error envelope. */
  | 'api'
  /** Non-JSON / non-envelope response (Caddy 502, HTML 404, …). */
  | 'transport'
  /** fetch() itself failed — offline, DNS, CORS misconfiguration. */
  | 'network'
  /** The request exceeded its timeout or was aborted. */
  | 'timeout'

export interface ApiErrorInit {
  readonly kind: ApiErrorKind
  readonly status: number
  readonly code: ErrorCode
  readonly message: string
  readonly details?: Readonly<Record<string, unknown>>
  readonly requestId?: string | undefined
  readonly cause?: unknown
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number
  readonly code: ErrorCode
  readonly details: Readonly<Record<string, unknown>>
  /** `details.code`, else `details.reason`, else undefined. */
  readonly subCode: string | undefined
  /** `request_id` from the body, else the `x-request-id` header. Show it in error UI. */
  readonly requestId: string | undefined

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause })
    this.name = 'ApiError'
    this.kind = init.kind
    this.status = init.status
    this.code = init.code
    this.details = init.details ?? {}
    this.subCode = readSubCode(this.details)
    this.requestId = init.requestId
  }

  /** True for `code`, or for `subCode` when the top-level code is generic. */
  is(code: string): boolean {
    return this.code === code || this.subCode === code
  }

  /** `details.fields` of a VALIDATION_ERROR: `{ field: string[] }` (ZodValidationPipe). */
  get fieldErrors(): Readonly<Record<string, readonly string[]>> {
    const fields = this.details['fields']
    if (typeof fields !== 'object' || fields === null) return {}
    const out: Record<string, readonly string[]> = {}
    for (const [key, value] of Object.entries(fields)) {
      if (Array.isArray(value))
        out[key] = value.filter((item): item is string => typeof item === 'string')
    }
    return out
  }

  /** `details.retry_after_seconds` when the server told us how long to wait. */
  get retryAfterSeconds(): number | undefined {
    const value = this.details['retry_after_seconds']
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined
  }
}

function readSubCode(details: Readonly<Record<string, unknown>>): string | undefined {
  const code = details['code']
  if (typeof code === 'string' && code !== '') return code
  const reason = details['reason']
  return typeof reason === 'string' && reason !== '' ? reason : undefined
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

const ERROR_CODE_SET: ReadonlySet<string> = new Set(ALL_ERROR_CODES)

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && ERROR_CODE_SET.has(value)
}

/** Structural check for the backend's error envelope. */
export function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  if (typeof value !== 'object' || value === null) return false
  const error: unknown = Reflect.get(value, 'error')
  if (typeof error !== 'object' || error === null) return false
  return (
    isErrorCode(Reflect.get(error, 'code')) && typeof Reflect.get(error, 'message') === 'string'
  )
}

/** Default top-level code for a status when the body carried no envelope (mirrors http-exception.filter.ts). */
export function codeForStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return 'BAD_REQUEST'
    case 401:
      return 'UNAUTHENTICATED'
    case 403:
      return 'FORBIDDEN'
    case 404:
      return 'NOT_FOUND'
    case 409:
      return 'CONFLICT'
    case 410:
      return 'GONE'
    case 422:
      return 'UNPROCESSABLE'
    case 429:
      return 'RATE_LIMITED'
    case 502:
      return 'INTEGRATION_UNAVAILABLE'
    case 503:
      return 'SERVICE_UNAVAILABLE'
    default:
      return 'INTERNAL_ERROR'
  }
}
